import * as React from "react";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";
import styled from "styled-components";

import Grid from "src/library/components/Hedron";
import DropInfoSidebar from "src/core/drops/components/DropInfoSidebar";
import { ListActionsList } from "src/core/lists/components/ListActionsList";
import {
  InfoBarWrapper,
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { listsTableColumns } from "src/core/shared/components/tables/listsTableColumns";
import {
  listsApi,
  IList,
  DropListParams,
  GetDropListsParams
} from "src/core/lists/api/lists.api";
import {
  FilterParams,
  PaginatedData,
  PaginatedParams
} from "src/core/shared/api";
import { setLists } from "src/core/lists/store/lists.actions";
import type { IStore } from "src/core/shared/store";
import { useDrop } from "src/core/drops/hooks";
import { PageProps } from "src/pages/Router/routes";
import useList from "src/core/lists/hooks/useList";
import { CreateListContext } from "src/core/shared/components/HeaderContext/CreateListContext";
import { usePriceVisibility } from "src/core/prices/hooks/use-price-visibility";
import { Table } from "src/core/dashboard/components/widgets";

interface UrlParams {
  dropId: string;
  customerId: string;
}

const defaultPagination: PaginatedParams<IList> = {
  page: 0,
  limit: 10,
  offset: 0,
  sort: [ [ "updatedAt", "DESC" ] ]
};

const ListsPage: React.ReactFCWithChildren<PageProps<unknown, UrlParams>> = props => {
  const dispatch = useDispatch();
  const { terminology, match } = props;
  const dropId = match ? parseInt(match.params.dropId, 0) : 0;
  const [ { isLoading: isLoadingDrop, drop } ] = useDrop(dropId);
  const [ { isCreatingList }, { createList } ] = useList();
  const [ listsResponse, getLists ] = listsApi<PaginatedData<IList>, DropListParams>("GET_DROP_LISTS");
  const listsData = listsResponse.data;
  const tableData = React.useMemo(() => listsData?.result || [], [ listsData ]);

  const { role, erp } = useMappedState(({ global }: IStore) => ({
    role: global.role,
    erp: global.settings && global.settings.erp.name
  }));

  const pricesVisible = usePriceVisibility();

  const onFetchData = React.useCallback((params: {filters: FilterParams<IList>, pagination: PaginatedParams<IList>}) => {
    if (dropId) {
      getLists<GetDropListsParams>({
        dropId,
        params
      });
    }
  }, [ dropId ]);

  React.useEffect(() => {
    if (listsData?.result) {
      dispatch(setLists(listsData.result));
    }
  }, [ dispatch, listsData?.result ]);

  const isLoading = listsResponse.pending;

  const tableColumns = React.useMemo(() => {
    if (!role || !erp || !pricesVisible || !terminology) {
      return [];
    }

    return listsTableColumns({
      actionButton: info => {
        if (role && erp) {
          return (
            <ListActionsList
              list={info.row.original}
              role={role}
              terminology={terminology}
              erp={erp}
            />
          );
        }

        return null;
      },
      pricesVisible,
      terminology
    });
  }, [
    erp,
    pricesVisible,
    role,
    terminology
  ]);

  return (
    <Layout {...props}>
      <MainContentWrapper fill>
        <ListCreationWrapper>
          <CreateListContext
            createList={createList}
            loading={isCreatingList}
          />
        </ListCreationWrapper>

        <Grid.Bounds direction="vertical">
          <Grid.Box>
            <Table
              tableOf={terminology.list.plural.text}
              className="large"
              data={tableData}
              onFetchData={onFetchData}
              defaultPagination={defaultPagination}
              defaultVisibility={{
                createdAt: false,
                updatedAt: false
              }}
              loading={isLoading}
              pageCount={listsData ? listsData.pages : 0}
              rowCount={listsData ? listsData.count : 0}
              error={listsResponse.error}
              columns={tableColumns}
              pageSizeOptions={[
                10,
                20,
                50,
                100
              ]}
            />
          </Grid.Box>
        </Grid.Bounds>
      </MainContentWrapper>

      <InfoBarWrapper>
        <DropInfoSidebar
          terminology={terminology}
          isLoading={isLoadingDrop}
          data={drop}
        />
      </InfoBarWrapper>
    </Layout>
  );
};

const ListCreationWrapper = styled(Grid.Bounds)`
  position: relative;
  padding: ${({ theme }) => theme.spacing.padding.md} 0;
`;

export default ListsPage;
